import React, {useState} from 'react';
import {Box, Button} from '@mui/material';
import {faArrowsRotate, faShield} from '@fortawesome/pro-light-svg-icons';
import {AlertBanner} from '@resurs-internal/mui';
import {useContactVerification} from '@resurs-internal/rb-app-api';
import {FontIcon} from '../../../components';
import {PinInput} from '../../../pages/accounts/credits/card/PinInput';
import {useTranslate} from '../../../hooks';
import type {VerifyContactFlow} from '../useVerifyContactFlow';
import {VerifyContactStepViewHeader} from './VerifyContactStepViewHeader';

export const VerifyContactStepViewCode: React.FC<{
  flow: VerifyContactFlow;
}> = ({flow}) => {
  const {
    channel,
    displayValue,
    code,
    setCode,
    verify,
    isBusy,
    isCodeComplete,
    verifyError,
  } = flow;
  const translate = useTranslate();
  const verification = useContactVerification();

  // Floors the resend spinner at ~800ms so the feedback is actually
  // perceivable on fast networks — the OTP send is usually a sub-100ms
  // round trip, which without this would just flicker.
  const [resendDelayActive, setResendDelayActive] = useState(false);
  const isResending = verification.isSending || resendDelayActive;

  const handleResend = async () => {
    setCode([]);
    setResendDelayActive(true);
    setTimeout(() => setResendDelayActive(false), 800);
    try {
      await verification.sendOtp(channel, displayValue);
    } catch {
      // Error surfaces via verification.sendError
    }
  };

  return (
    <Box
      onKeyDown={(e) => {
        if (e.key === 'Enter' && isCodeComplete && !isBusy) verify();
      }}>
      <VerifyContactStepViewHeader
        icon={faShield}
        title={translate('verify-contact.code.title')}
        subtitle={translate('verify-contact.code.description', {
          target: displayValue,
        })}
      />

      <Box sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
        <PinInput
          value={code}
          visible={true}
          length={6}
          spacing={1.5}
          size={48}
          onChange={setCode}
        />
      </Box>

      <Box sx={{display: 'flex', justifyContent: 'center', mb: 1.5}}>
        <Button
          variant={'text'}
          size={'small'}
          onClick={handleResend}
          disabled={isResending}
          startIcon={
            <FontIcon
              icon={faArrowsRotate}
              size={14}
              style={{
                animation: isResending ? 'spin 0.8s linear infinite' : 'none',
              }}
            />
          }
          sx={{
            fontWeight: 700,
            '@keyframes spin': {
              from: {transform: 'rotate(0deg)'},
              to: {transform: 'rotate(360deg)'},
            },
          }}>
          {translate(
            isResending
              ? 'click.to.pay.verify.resend-code.sending'
              : 'click.to.pay.verify.resend-code',
          )}
        </Button>
      </Box>

      {verifyError && (
        <AlertBanner
          severity={'error'}
          title={translate('verify-contact.code.error')}
        />
      )}
      {verification.sendError && !verifyError && (
        <AlertBanner
          severity={'error'}
          title={translate('click.to.pay.verify.send-failed')}
        />
      )}
    </Box>
  );
};
